import type { FormInstance } from 'antd'
import { App } from 'antd'
import type { NotificationInstance } from 'antd/es/notification/interface'
import { AxiosError } from 'axios'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

export type ShowApiErrorsOptions = {
  allowedFields?: string[]
  /** Localized replacement for {@link UNEXPECTED_API_ERROR}. */
  fallbackMessage?: string
}

/** Fallback shown when the error shape is not a recognized API error body. */
export const UNEXPECTED_API_ERROR = 'An unexpected error occurred.'

/**
 * Keys carrying server internals, not user-facing text. Core's 500 handler ships
 * `{ exception: <raw exception string>, detail: <human message> }`, so rendering
 * every key put database constraint names in front of the user.
 */
const TECHNICAL_ERROR_KEYS = new Set([
  'exception',
  'traceback',
  'stack',
  'stacktrace'
])

/** Keys that already read as a sentence — showing them prefixed adds noise. */
const GENERIC_MESSAGE_KEYS = new Set([
  'detail',
  'details',
  'error',
  'errors',
  'message',
  'non_field_errors'
])

/** `domain_name` → `Domain name`, for messages that keep their field prefix. */
const fieldLabel = (field: string) => {
  const spaced = field.replace(/_/g, ' ')

  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export type ParsedApiErrors = {
  /** Errors that map to an existing form field — set inline via `setFields`. */
  fieldErrors: { name: string; errors: string[] }[]
  /** Non-field messages to surface at the page level (notification or Alert). */
  messages: string[]
  /** True when the error shape was unrecognized → use {@link UNEXPECTED_API_ERROR}. */
  unexpected: boolean
}

/**
 * Normalize an unknown error (AxiosError, hey-api body, string, array, object)
 * into form-field errors and page-level messages. Pure — the caller decides how
 * to surface the result (notification, inline Alert, router state, …).
 */
export const parseApiErrors = (
  error: unknown,
  form?: FormInstance,
  options?: ShowApiErrorsOptions
): ParsedApiErrors => {
  let errorData: unknown

  if (error instanceof AxiosError && error.response) {
    errorData = error.response.data
  } else if (
    typeof error === 'object' &&
    error !== null &&
    !('stack' in error)
  ) {
    // hey-api client returns the response body directly as the error
    errorData = error
  } else {
    return { fieldErrors: [], messages: [], unexpected: true }
  }

  const fieldErrors: { name: string; errors: string[] }[] = []
  const messages: string[] = []
  const allowedFieldsSet = options?.allowedFields?.length
    ? new Set(options.allowedFields)
    : null

  if (typeof errorData === 'string') {
    messages.push(errorData)
  } else if (Array.isArray(errorData)) {
    messages.push(
      ...errorData.filter((msg): msg is string => typeof msg === 'string')
    )
  } else if (typeof errorData === 'object' && errorData !== null) {
    for (const [field, value] of Object.entries(errorData)) {
      if (allowedFieldsSet && !allowedFieldsSet.has(field)) {
        continue
      }

      if (TECHNICAL_ERROR_KEYS.has(field)) {
        continue
      }

      const fieldMessages: string[] = Array.isArray(value)
        ? value.filter((msg): msg is string => typeof msg === 'string')
        : typeof value === 'string'
          ? [value]
          : []

      if (!fieldMessages.length) continue

      if (form?.getFieldInstance?.(field)) {
        fieldErrors.push({ name: field, errors: fieldMessages })
      } else if (GENERIC_MESSAGE_KEYS.has(field)) {
        messages.push(...fieldMessages)
      } else {
        fieldMessages.forEach((msg) => {
          messages.push(`${fieldLabel(field)}: ${msg}`)
        })
      }
    }
  }

  // Everything was internals or filtered out: the request still failed, so fall
  // back to the generic message rather than leaving the user with a blank alert.
  if (!fieldErrors.length && !messages.length) {
    return { fieldErrors, messages, unexpected: true }
  }

  return { fieldErrors, messages, unexpected: false }
}

/**
 * Extract just the page-level messages from an unknown error (no form routing).
 * Useful when an error must be carried elsewhere to be displayed — e.g. a social
 * callback that redirects to the login screen and surfaces the failure there.
 */
export const getApiErrorMessages = (error: unknown): string[] => {
  const { messages, unexpected } = parseApiErrors(error)

  return unexpected ? [UNEXPECTED_API_ERROR] : messages
}

/**
 * Show API errors in a notification (use with a {@link NotificationInstance} from `App.useApp()`).
 * Prefer {@link useShowApiErrors} in components and hooks under `<App>`.
 */
export const showApiErrorsWithNotification = (
  notification: NotificationInstance,
  error: unknown,
  form?: FormInstance,
  options?: ShowApiErrorsOptions
): void => {
  const { fieldErrors, messages, unexpected } = parseApiErrors(
    error,
    form,
    options
  )

  if (unexpected) {
    notification.error({
      title: options?.fallbackMessage ?? UNEXPECTED_API_ERROR,
      duration: 2
    })
    return
  }

  if (form && fieldErrors.length) {
    form.setFields(
      fieldErrors.map(({ name, errors }) => ({
        name,
        errors
      }))
    )
  }

  const shown = new Set<string>()
  for (const msg of messages) {
    if (!shown.has(msg)) {
      shown.add(msg)
      notification.error({
        title: msg,
        duration: 2
      })
    }
  }
}

/** Must be used under Ant Design `<App>` (see root `App.tsx`). */
export const useShowApiErrors = () => {
  const { notification } = App.useApp()
  const { t } = useTranslation()

  return useCallback(
    (error: unknown, form?: FormInstance, options?: ShowApiErrorsOptions) =>
      showApiErrorsWithNotification(notification, error, form, {
        ...options,
        fallbackMessage:
          options?.fallbackMessage ?? t('errors.somethingWentWrong')
      }),
    [notification, t]
  )
}
